import axios from "../../../axios";
import { createSlice } from "@reduxjs/toolkit";
import { routesByTrailerIdT, Route } from "./types";
import * as tPromise from "io-ts-promise";
import * as dataActions from "../../data/trailers";
import * as dialogActions from "../trailer-dialog";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { notifyL } from "../../notifications";
import { CANNOT_ARCHIVE_TRAILER_WITH_ROUTES } from "dora-shared";
import { AppThunkAction } from "../../../redux-store";
import { userStorage } from "../../../helpers/user-storage";

const prefix = "/app/FleetManagementPage";

// Saved per user and restored once the user is known (see checkAuthState).
export const FLEET_MANAGEMENT_TEAMS_FILTER = "fleetManagementPageTeamsFilter";

interface State {
  routes: { [key: string]: Route[] };
  filterTeamIds: string[];
  status: "UNINITIALIZED" | "LOADING" | "LOADED";
}

const initialState: State = {
  routes: {},
  status: "UNINITIALIZED",
  filterTeamIds: [],
};

const decodeRoutesByTrailerId = tPromise.decode(routesByTrailerIdT);

export const editTrailer = createErrorReportingAsyncThunk(
  `${prefix}/edit-trailer`,
  (trailerId: string, thunkApi) => {
    const trailer = thunkApi.getState().data.trailers.entities[trailerId];
    thunkApi.dispatch(dialogActions.editTrailer(trailer));
  }
);

export const archiveTrailer = createErrorReportingAsyncThunk(
  `${prefix}/archive-trailer`,
  (trailerId: string, thunkAPI) => {
    return axios
      .post(`/api/trailers/${trailerId}/archive`)
      .then(() => {
        thunkAPI.dispatch(dataActions.loadTrailers());
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "vehicleArchived",
          })
        );
      })
      .catch((err) => {
        if (
          err.response.status === 400 &&
          err.response.data.message === CANNOT_ARCHIVE_TRAILER_WITH_ROUTES
        ) {
          thunkAPI.dispatch(
            notifyL({
              type: "error",
              namespace: "notifications",
              key: "cannotArchiveAssignedVehicle",
            })
          );
          return;
        }
        throw err;
      });
  }
);

export const activateTrailer = createErrorReportingAsyncThunk(
  `${prefix}/activate`,
  async (id: string, thunkAPI) => {
    return axios.post(`/api/trailers/${id}/activate`).then(() => {
      thunkAPI.dispatch(dataActions.loadTrailers());
      thunkAPI.dispatch(
        notifyL({
          type: "success",
          namespace: "notifications",
          key: "vehicleReactivated",
        })
      );
    });
  }
);

export const saveTrailer = dataActions.updateTrailer;

export const getTrailers = createErrorReportingAsyncThunk(
  `${prefix}/get-trailers`,
  async (_: void, thunkApi) => {
    const trailers = await thunkApi
      .dispatch(dataActions.loadTrailers())
      .unwrap();

    let routesByTrailerId = {};
    if (trailers.length) {
      const axiosResult = await axios.get("/api/trailers/routes", {
        params: { ids: trailers.map(({ id }) => id).join(",") },
      });
      routesByTrailerId = await decodeRoutesByTrailerId(axiosResult.data);
    }
    thunkApi.dispatch(slice.actions.setRoutes(routesByTrailerId));

    return trailers;
  }
);

export const toggleTeamFilterAndSave =
  (payload: string): AppThunkAction =>
  (dispatch, getState) => {
    dispatch(slice.actions.toggleTeamFilter(payload));
    const updatedFilterTeamIds = getState().app.fleetManagement.filterTeamIds;
    userStorage.setItem(
      FLEET_MANAGEMENT_TEAMS_FILTER,
      JSON.stringify(updatedFilterTeamIds)
    );
  };

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    toggleTeamFilter(state, action) {
      const id = action.payload;
      if (!state.filterTeamIds.includes(id)) {
        state.filterTeamIds.push(id);
      } else {
        state.filterTeamIds = state.filterTeamIds.filter(
          (teamId) => teamId !== id
        );
      }
    },
    setSelectedTeams(state, action) {
      state.filterTeamIds = action.payload;
    },
    setRoutes(state, action) {
      state.routes = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTrailers.pending, (state) => {
        state.status = "LOADING";
      })
      .addCase(getTrailers.fulfilled, (state) => {
        state.status = "LOADED";
      });
  },
});

export const { setSelectedTeams } = slice.actions;

export default slice.reducer;
